import { useContext, useState, type CSSProperties } from 'react';
import { SelectedStylepackContext } from './stylepack-context';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import styles from './stylepacks.module.css';
import IconToggle from '@ui/icon-toggle/icon-toggle';
import { ICONS } from '@ui/icons/types';
import VideoCard from './components/video-card';
import VideoListItem from './components/video-list-item';
import OptionalComponent from '@ui/optional-component/optional-component';
import { useAppSelector } from '@/store/hooks';
import {
  selectGreenroomMeetingLoading,
  selectGreenroomShots,
  selectGreenroomShotsLoading,
} from '@/store/slices/project/selectors';
import { VideoType } from '@/types/stylepacks.types';
import SelectSingle from '@ui/select/react-select/select-single';
import Button from '@ui/buttons/button/button';
import { useHandleGoToGreenroom } from '@ui/greenroom-button/use-handle-go-to-greenroom';
import { useGreenroomStylepackShots } from './utils/use-greenroom-stylepack-shots';

enum View {
  cards,
  list,
}

const typeOptions: { label: string; value: string }[] = Object.values(VideoType).map((value) => ({
  label: String(value),
  value: String(value),
}));

export default function StylepackContent() {
  const { stylepack } = useContext(SelectedStylepackContext);
  const greenroomShots = useAppSelector(selectGreenroomShots);
  const greenroomShotsLoading = useAppSelector(selectGreenroomShotsLoading);
  const greenroomMeetingLoading = useAppSelector(selectGreenroomMeetingLoading);

  const [type, setType] = useState(VideoType.all);
  const [view, setView] = useState<View>(View.cards);
  const filteredVideos = useGreenroomStylepackShots(stylepack, type);
  const handleGoToGreenroom = useHandleGoToGreenroom();

  const loading = greenroomShotsLoading || greenroomMeetingLoading;

  const empty = filteredVideos.length === 0;

  return (
    <Container
      width="100%"
      padding="0px 40px"
      className={styles.content}
      style={
        {
          '--bloom-image': empty ? undefined : `url(${stylepack.videos[0]?.thumbnailUrl})`,
          '--bloom-color': empty ? 'var(--product-acc-400)' : 'transparent',
        } as CSSProperties
      }
    >
      <Container className="flex ai-center jc-space-between margin-b-24">
        <Text className="H4" tag="h4">
          Graphics
        </Text>
        <Container className="flex ai-center gap-8">
          <SelectSingle
            options={typeOptions}
            placeholder="Sort by"
            value={type}
            forcePlaceholder
            onChange={(option: string) => {
              setType((option as VideoType) ?? VideoType.all);
            }}
          />
          <IconToggle
            leftValue={View.cards}
            leftIconName={ICONS.LAYOUT_GRID_4}
            rightValue={View.list}
            rightIconName={ICONS.LAYOUT_GRID_2}
            onToggle={() => setView((prev) => (prev === View.cards ? View.list : View.cards))}
            selected={view}
          />
        </Container>
      </Container>
      <OptionalComponent display={view === View.list}>
        <Container className={styles.listItem} padding="0px 16px 8px 8px" loading={loading}>
          <div />
          <Text className="L3" design="GlobalIcoText3" transform="uppercase">
            Title
          </Text>
          <Text className="L3" design="GlobalIcoText3" transform="uppercase">
            {greenroomShots ? 'Shots' : ''}
          </Text>
          <Text className="L3" design="GlobalIcoText3" transform="uppercase">
            Duration
          </Text>
          <div />
        </Container>
      </OptionalComponent>
      <OptionalComponent display={!empty}>
        <Container className={`grid ${view === View.cards ? styles.contentGrid : styles.contentList}`}>
          {filteredVideos.map((video) => {
            return view === View.cards ? (
              <VideoCard key={video.id} {...video} />
            ) : (
              <VideoListItem key={video.id} {...video} />
            );
          })}
        </Container>
      </OptionalComponent>
      <OptionalComponent display={empty}>
        <Container
          width="100%"
          height="calc(100svh - 448px - 96px)"
          className="flex ai-center jc-center flex-column gap-32"
        >
          <Text className="H3" tag="h3">
            Nothing to see here... yet!
          </Text>
          <Button onClick={handleGoToGreenroom} design="LocalBttnPrim" designSize="large">
            Start adding in Greenroom
          </Button>
        </Container>
      </OptionalComponent>
    </Container>
  );
}
